'use client';

import React, { useState } from 'react';
import { cn } from '@/lib/utils';
import { PortableTextBlock } from '@portabletext/types';

const DEFAULT_WORD_LIMIT = 55;
const CLAMP_LINES = 5;

function countWords(blocks: PortableTextBlock[]): number {
  let words = 0;
  for (const block of blocks) {
    if (block._type === 'block' && Array.isArray(block.children)) {
      for (const child of block.children) {
        if (typeof child.text === 'string') {
          words += child.text.split(/\s+/).filter(Boolean).length;
        }
      }
    }
  }
  return words;
}

interface ExpandableTextProps {
  blocks: PortableTextBlock[];
  /** Collapse when content exceeds this many words (default: 45) */
  wordLimit?: number;
  /** Number of visible lines when collapsed (default: 5) */
  lineClamp?: number;
  className?: string;
  children: React.ReactNode;
}

export function ExpandableText({
  blocks,
  wordLimit = DEFAULT_WORD_LIMIT,
  lineClamp = CLAMP_LINES,
  className,
  children,
}: ExpandableTextProps) {
  const [expanded, setExpanded] = useState(false);

  if (countWords(blocks) <= wordLimit) {
    return <div className={className}>{children}</div>;
  }

  return (
    <div className={className}>
      <div
        className={cn(!expanded && 'line-clamp-(--clamp)')}
        style={!expanded ? ({ '--clamp': lineClamp } as React.CSSProperties) : undefined}
      >
        {children}
      </div>
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="mt-2 text-sm font-medium text-primary hover:text-primary/80 transition-colors"
      >
        {expanded ? 'Read less' : 'Read more'}
      </button>
    </div>
  );
}
